import type { OversightRatingByObligation } from '@risksmart-app/trpc/src/types';
import type { GetLatestOversightRatingsByObligationIdsQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetLatestOversightRatingsByObligationIdsDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook, type StrictMappedGraphQLSelection } from 'src/utils';

type UseGetLatestOversightRatingsByObligationIdsArgs = {
  obligationIds: string[];
};

/**
 * Reshapes the service-resolved "latest rating per obligation" rows back into
 * the GraphQL query response shape so the page hook's client-side resolution
 * (which both branches share) treats them uniformly. The service already
 * reduced each obligation to a single leaf, so one synthetic
 * `obligation_oversight_result` row per obligation is emitted; the shared
 * resolver then trivially selects it. `Id` and `TestDate` are unused by the
 * resolver for a single-row-per-parent input, so placeholders are safe.
 */
export const mapOversightRatingsByObligationToGraphQL = (
  data: OversightRatingByObligation[]
): StrictMappedGraphQLSelection<GetLatestOversightRatingsByObligationIdsQuery> => ({
  obligation_oversight_result: data.map((row) => ({
    Id: '',
    Rating: row.Rating,
    TestDate: null,
    CreatedAtTimestamp: row.CreatedAt ?? '',
    obligationParent: [{ ParentId: row.ObligationId }],
  })),
});

export const useGetLatestOversightRatingsByObligationIds = createQueryHook<
  UseGetLatestOversightRatingsByObligationIdsArgs,
  OversightRatingByObligation[],
  GetLatestOversightRatingsByObligationIdsQuery
>({
  trpcQueryOptions: (trpc, { obligationIds }) =>
    trpc.frontend.oversight.ratingsByObligationIds.queryOptions({
      obligationIds,
    }),
  mapTrpcDataToGraphQL: mapOversightRatingsByObligationToGraphQL,
  graphqlDocument: GetLatestOversightRatingsByObligationIdsDocument,
  graphqlVariables: ({ obligationIds }) => ({ ObligationIds: obligationIds }),
  graphqlFetchPolicy: 'cache-and-network',
});
